import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

import { TableChartConfigDto, BatterSessionStatsByPitchTypeChartDataDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import useReportStore from 'hooks/SessionReport/BatterSingle/reportStore';
import PitchTypeTableCell from 'components/Table/PitchTypeTableCell';

export type BatterSessionStatsByPitchTypeChartColumn = keyof BatterSessionStatsByPitchTypeChartDataDto['statsByPitchType'][number];

const BatterSessionStatsByPitchTypeChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<BatterSessionStatsByPitchTypeChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<BatterSessionStatsByPitchTypeChartColumn | '' | 'id'>('');

  const { selectedConfgValues } = useReportStore();

  const rows = useMemo(
    () =>
      chartData?.statsByPitchType.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.statsByPitchType]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<BatterSessionStatsByPitchTypeChartColumn>(chartConfig.visibleStats);
    const pitchTypeColumnNameKey = selectedConfgValues.pitchGrouping === 'Types' ? 'pitchType' : 'parentPitchType';

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: column === 'pitchType' ? t(pitchTypeColumnNameKey) : t(column),
      headerShortName: column === 'pitchType' ? t('short.' + pitchTypeColumnNameKey) : t('short.' + column),
      sortable: true,
      renderCell: column === 'pitchType' ? (cellValue) => <PitchTypeTableCell pitchType={cellValue} /> : undefined,
    }));
  }, [chartConfig.visibleStats, selectedConfgValues.pitchGrouping]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default BatterSessionStatsByPitchTypeChart;
